import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { NewsArticle } from '../../../hooks/useNewsArticles';

interface TrendingTickerProps {
  articles?: NewsArticle[];
}

export default function TrendingTicker({ articles }: TrendingTickerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  // Use the first 8 articles for the ticker
  const tickerArticles = articles ? articles.slice(0, 8) : [];

  // Duplicate for seamless loop
  const items = [...tickerArticles, ...tickerArticles];

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || isPaused || items.length === 0) return;

    let animationId: number;
    let scrollPos = 0;

    const scroll = () => {
      scrollPos += 0.6;
      if (scrollPos >= container.scrollWidth / 2) {
        scrollPos = 0;
      }
      container.scrollLeft = scrollPos;
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused, items.length]);

  if (!articles || articles.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-900 rounded-xl mb-8 overflow-hidden">
      <div className="flex items-center">
        {/* Label */}
        <div className="flex-shrink-0 flex items-center gap-2 px-5 py-3 bg-red-600">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          <span className="text-white text-xs font-bold uppercase tracking-wider whitespace-nowrap">Trending Now</span>
        </div>

        {/* Scrolling headlines */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          style={{ scrollbarWidth: 'none' }}
        >
          <div className="flex items-center gap-0 whitespace-nowrap py-3">
            {items.map((article, idx) => (
              <Link
                key={`${article.id}-${idx}`}
                to={`/news/${article.id}`}
                className="inline-flex items-center gap-2 px-5 text-sm text-gray-300 hover:text-teal-400 transition-colors cursor-pointer flex-shrink-0"
              >
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0 animate-pulse"></span>
                <span className="font-medium">{article.title}</span>
                <span className="text-gray-600 mx-3">|</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
